import React, { memo, useCallback, useMemo } from 'react';
import { Doc, DocTypes } from '@app/models';
import { Button } from '../../components/ui/button';
import { Input } from 'components/ui/input';
import { Card } from 'components/ui/card';
import { useForm } from '../hooks';
import { date, object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@app/components';

interface Props {
  onSubmit: (doc: Partial<Doc>) => void;
  type: DocTypes;
}

interface FormData {
  name: string;
  price: string;
  description: string;
  endDate: Date | null;
}

function FormDoc(props: Props) {
  const { onSubmit, type } = props;
  const { t } = useTranslation();
  const { values, onChange, errors, validate } = useForm<FormData>({
    name: '',
    price: '',
    description: '',
    endDate: null,
  });

  const onClickSubmit = useCallback(async () => {
    try {
      const hasErrors = await validate(
        object().shape({
          name: string().required(t('required', { ns: 'validation' })!),
          price: string().required(t('required', { ns: 'validation' })!),
          description: string().required(t('required', { ns: 'validation' })!),
          endDate: date().required(t('required', { ns: 'validation' })!),
        })
      );

      if (hasErrors) {
        return;
      }

      onSubmit({
        name: values.name,
        price: values.price,
        description: values.description,
        endDate: values.endDate!.toJSON(),
      });
    } catch (e) {}
  }, [onSubmit, t, validate, values]);

  const label = useMemo(() => {
    return type === DocTypes.CONTRACT ? 'договора' : 'документа';
  }, [type]);

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label htmlFor="name" className="block mb-1 text-sm font-medium">
              Предмет {label}
            </label>
            <Input
              id="name"
              value={values.name}
              name="name"
              onChange={(e) => onChange(e.target.value, 'name')}
              placeholder={`Введите предмет ${label}`}
              required
            />
            {errors.name && (
              <span className="text-red-500 text-xs mt-1">{errors.name}</span>
            )}
          </div>
          <div>
            <label htmlFor="endDate" className="block mb-1 text-sm font-medium">
              Cрок {label}
            </label>
            <DatePicker
              value={values.endDate}
              name="endDate"
              onChange={onChange}
              placeholderText="Выберите срок"
              required={true}
              minDate={new Date()}
            />
            {errors.endDate && (
              <span className="text-red-500 text-xs mt-1">
                {errors.endDate}
              </span>
            )}
          </div>
          <div>
            <label htmlFor="price" className="block mb-1 text-sm font-medium">
              Сумма {label}
            </label>
            <Input
              id="price"
              value={values.price}
              onChange={(e) => onChange(e.target.value, 'price')}
              name="price"
              placeholder={`Введите сумму ${label}`}
              required
            />
            {errors.price && (
              <span className="text-red-500 text-xs mt-1">{errors.price}</span>
            )}
          </div>
        </div>
        <div>
          <label
            htmlFor="description"
            className="block mb-1 text-sm font-medium"
          >
            Краткое содержание
          </label>
          <Input
            id="description"
            value={values.description}
            onChange={(e) => onChange(e.target.value, 'description')}
            name="description"
            placeholder="Введите краткое содержание"
            required
          />
          {errors.description && (
            <span className="text-red-500 text-xs mt-1">
              {errors.description}
            </span>
          )}
        </div>
      </Card>
      <div className="flex justify-end px-6">
        <Button onClick={onClickSubmit}>Продолжить</Button>
      </div>
    </div>
  );
}

export default memo(FormDoc);
